import { formatCurrencyEnUs } from '@zorro/shared/formatters';
import {
  Box,
  CurrencyInput,
  FormErrorMessage,
  Grid,
  Stack,
  Text,
} from '@zorro/web/design-system';
import { Controller, useForm } from 'react-hook-form';

import { useAnalytics } from '../Analytics';
import { EnrollmentConfirmationFormFields } from './EnrollmentConfirmationUtils';

type Props = {
  enrollmentConfirmationForm: ReturnType<
    typeof useForm<EnrollmentConfirmationFormFields>
  >;
  employerMonthlyContribution: number;
  onboardingPeriodId?: string;
};

export const ConfirmationPremiumAmountFormInputs = ({
  enrollmentConfirmationForm,
  employerMonthlyContribution,
  onboardingPeriodId,
}: Props) => {
  const { sendEvent } = useAnalytics();

  const { control, formState, watch } = enrollmentConfirmationForm;
  const { errors } = formState;

  const { premium, employeeMonthlyContribution } = watch();

  return (
    <Box
      p="lg"
      style={(theme) => ({
        border: `1px solid ${theme.colors.zorroGray[2]}`,
        borderRadius: theme.radius.md,
      })}
      w="100%"
    >
      <Grid>
        <Grid.Col span={{ base: 12, md: 3.4 }} pos="relative">
          <Text fw={600}>Monthly breakdown</Text>
        </Grid.Col>
        <Grid.Col span={2}>
          <Stack gap="xs">
            <Text fw={600} size="xl">
              {formatCurrencyEnUs(employerMonthlyContribution)}
            </Text>
            <Text c="zorroGray.4">Employer</Text>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 0.8 }}>
          <Text fw={600} size="lg">
            +
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 2 }}>
          <Stack gap="xs">
            <Text fw={600} size="xl">
              {formatCurrencyEnUs(employeeMonthlyContribution)}
            </Text>
            <Text c="zorroGray.4">Employee</Text>
          </Stack>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 0.8 }}>
          <Text fw={600} size="lg">
            =
          </Text>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 3 }}>
          <Stack gap="xs">
            <Controller
              control={control}
              name="premium"
              render={({ field: { ...rest } }) => (
                <CurrencyInput
                  {...rest}
                  decimalScale={2}
                  prefix="$"
                  onBlur={async () => {
                    if (formState.defaultValues?.premium !== premium) {
                      await sendEvent('edit_premium', {
                        onboarding_period_id: onboardingPeriodId,
                        from_premium: formState.defaultValues?.premium || 0,
                        to_premium: premium,
                      });
                    }
                  }}
                />
              )}
            />
            <Text c="zorroGray.4">Total premium</Text>
          </Stack>

          <FormErrorMessage errors={errors} fieldName="premium" />
        </Grid.Col>
      </Grid>
    </Box>
  );
};
